import React, { useCallback, useMemo } from "react";
import { FieldProps } from "react-jsonschema-form";
import InputGroup from "../../witadmin/CRUD/Common/InputGroup";
import styled from "styled-components";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import SelectGroup from "../../witadmin/CRUD/Common/SelectGroup";
import { WitLabel } from "./common";
import { Taxation } from "../../../gen/witbooking/backoffice/taxation/v1/taxation_api_pb";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { APP_COLORS } from "../../witadmin/Common/constants";

const StyledTooltip = styled(Tooltip)`
  .tooltip-inner {
    background-color: ${APP_COLORS["secondary"]};
  }

  .arrow::before {
    border-right-color: ${APP_COLORS["secondary"]};
  }
`;

const messages = defineMessages({
  perPerson: {
    id: "taxation.perPerson",
    defaultMessage: "perPerson",
  },
  perPersonPerNight: {
    id: "taxation.perPersonPerNight",
    defaultMessage: "perPersonPerNight",
  },
  perStay: {
    id: "taxation.perStay",
    defaultMessage: "perStay",
  },
  perStayPerNight: {
    id: "taxation.perStayPerNight",
    defaultMessage: "perStayPerNight",
  },
  percentage: {
    id: "taxation.percentage",
    defaultMessage: "percentage",
  },
  balearic: {
    id: "taxation.balearic",
    defaultMessage: "balearic",
  },
  amount: {
    id: "taxation.amount",
    defaultMessage: "amount",
  },
  amountPerNight: {
    id: "taxation.amountPerNight",
    defaultMessage: "amountPerNight",
  },
  quantity: {
    id: "taxation.quantity",
    defaultMessage: "quantity",
  },
  balearicFeeHelpText: {
    id: "taxation.balearicFeeHelpText",
    defaultMessage: "balearicFeeHelpText",
  },
  minAge: {
    id: "taxation.minAge",
    defaultMessage: "minAge",
  },
  minAgeHelpText: {
    id: "taxation.minAgeHelpText",
    defaultMessage: "minAgeHelpText",
  },
  percentageHelpTextTax: {
    id: "taxation.percentageHelpTextTax",
    defaultMessage: "percentageHelpText",
  },
  percentageHelpTextFee: {
    id: "taxation.percentageHelpTextFee",
    defaultMessage: "percentageHelpText",
  },
  tooltipTypologyBalearicRow1: {
    id: "taxation.tooltipTypologyBalearicRow1",
    defaultMessage: "tooltipTypologyBalearicRow1",
  },
  tooltipTypologyBalearicRow2: {
    id: "taxation.tooltipTypologyBalearicRow2",
    defaultMessage: "tooltipTypologyBalearicRow2",
  },
  maxNights: { id: "taxation.maxNights", defaultMessage: "maxNights" },
  maxNightsHelpText: { id: "taxation.maxNightsHelpText", defaultMessage: "maxNightsHelpText" },
  maxAmount: { id: "taxation.maxAmount", defaultMessage: "maxAmount" },
  maxAmountHelpText: { id: "taxation.maxAmountHelpText", defaultMessage: "maxAmountHelpText" },
  restrictions: { id: "taxation.restrictions", defaultMessage: "Restrictions" },
});

const TYPE = {
  FEE: "FEE",
  TAX: "TAX",
};

const SelectGroupWrapper = styled.div`
  width: 200px;
`;

export function TaxationTypologyManagerTax(props) {
  return <TaxationTypologyManager {...props} type={TYPE.TAX} />;
}

export function TaxationTypologyManagerFee(props) {
  return <TaxationTypologyManager {...props} type={TYPE.FEE} validateRestriction />;
}

function TaxationTypologyManager({
  formData,
  onChange,
  schema,
  type,
  validateRestriction,
}: FieldProps) {
  const intl = useIntl();
  const handleChange = useCallback(
    (data, type) => {
      onChange({
        ...formData,
        [type]: Number(data.event.target.value),
      });
    },
    [formData, onChange]
  );

  const inputLabel = useMemo(() => {
    switch (formData.typology) {
      case Taxation.TaxationTypology.PER_PERSON:
      case Taxation.TaxationTypology.PER_STAY:
        return (
          <label>
            <FormattedMessage {...messages.amount} />
          </label>
        );
      case Taxation.TaxationTypology.PER_PERSON_PER_NIGHT:
      case Taxation.TaxationTypology.PER_STAY_PER_NIGHT:
      case Taxation.TaxationTypology.BALEARIC:
        return (
          <label>
            <FormattedMessage {...messages.amountPerNight} />
            {formData.typology === 5 && "*"}
          </label>
        );
      case Taxation.TaxationTypology.PERCENTAGE:
        return (
          <label>
            <FormattedMessage {...messages.quantity} />*
          </label>
        );
      default:
        return (
          <label>
            {type === TYPE.TAX ? (
              <>
                <FormattedMessage {...messages.quantity} />*
              </>
            ) : (
              <FormattedMessage {...messages.amount} />
            )}
          </label>
        );
    }
  }, [formData.typology, type]);

  const optionDisplay =
    type === TYPE.FEE
      ? {
          [Taxation.TaxationTypology.PER_PERSON]: {
            label: intl.formatMessage(messages.perPerson),
          },
          [Taxation.TaxationTypology.PER_PERSON_PER_NIGHT]: {
            label: intl.formatMessage(messages.perPersonPerNight),
          },
          [Taxation.TaxationTypology.PER_STAY]: {
            label: intl.formatMessage(messages.perStay),
          },
          [Taxation.TaxationTypology.PER_STAY_PER_NIGHT]: {
            label: intl.formatMessage(messages.perStayPerNight),
          },
          [Taxation.TaxationTypology.PERCENTAGE]: {
            label: intl.formatMessage(messages.percentage),
          },
          [Taxation.TaxationTypology.BALEARIC]: {
            label: intl.formatMessage(messages.balearic),
          },
        }
      : {
          [Taxation.TaxationTypology.PERCENTAGE]: {
            label: intl.formatMessage(messages.percentage),
          },
        };

  const defaultTypology = useMemo(
    () =>
      type === TYPE.TAX
        ? Taxation.TaxationTypology.PERCENTAGE
        : Taxation.TaxationTypology.PER_PERSON,
    [type]
  );

  const tooltipConfig = useMemo(
    () => ({
      delay: { show: 250, hide: 0 },
    }),
    []
  );

  const infoTooltip = useMemo(
    () => (
      <StyledTooltip id="tooltipTypologyBalearic">
        <FormattedMessage {...messages.tooltipTypologyBalearicRow1} />
        <br />
        <br />
        <FormattedMessage {...messages.tooltipTypologyBalearicRow2} />
      </StyledTooltip>
    ),
    []
  );

  return (
    <>
      <WitLabel>{schema?.title}</WitLabel>
      <div>
        <SelectGroupWrapper>
          <SelectGroup
            value={formData.typology ?? defaultTypology}
            field={{
              fieldName: "typology",
              widget: "selector",
              optionDisplay,
            }}
            onChange={data => handleChange(data, "typology")}
            errors={undefined}
            additionalFields={undefined}
            className={undefined}
          />
        </SelectGroupWrapper>
        <div>
          {inputLabel}
          <InputGroup
            type="number"
            value={formData.amount}
            field={{ fieldName: "amount" }}
            onChange={data => handleChange(data, "amount")}
            disabled={false}
            readOnly={false}
          />
          {(!formData.typology || formData.typology === Taxation.TaxationTypology.PERCENTAGE) &&
            type === TYPE.TAX && (
              <label>
                *<FormattedMessage {...messages.percentageHelpTextTax} />
              </label>
            )}
          {(!formData.typology || formData.typology === Taxation.TaxationTypology.PERCENTAGE) &&
            type === TYPE.FEE && (
              <label>
                *<FormattedMessage {...messages.percentageHelpTextFee} />
              </label>
            )}
          {formData.typology === Taxation.TaxationTypology.BALEARIC && (
            <label>
              *<FormattedMessage {...messages.balearicFeeHelpText} />
              <OverlayTrigger {...tooltipConfig} overlay={infoTooltip} placement={"right"}>
                <FontAwesomeIcon icon={faInfoCircle} size="1x" className="ml-2" />
              </OverlayTrigger>
            </label>
          )}
        </div>
        {type === TYPE.FEE &&
          (!formData.typology ||
            formData.typology === Taxation.TaxationTypology.PER_PERSON ||
            formData.typology === Taxation.TaxationTypology.PER_PERSON_PER_NIGHT) && (
            <div>
              <label>
                <FormattedMessage {...messages.minAge} />*
              </label>
              <InputGroup
                type="number"
                value={formData.minAge}
                field={{ fieldName: "minAge" }}
                onChange={data => handleChange(data, "minAge")}
                disabled={false}
                readOnly={false}
              />
              <label>
                *<FormattedMessage {...messages.minAgeHelpText} />
              </label>
            </div>
          )}
        {validateRestriction && formData.typology !== Taxation.TaxationTypology.BALEARIC && (
          <>
            <WitLabel>{intl.formatMessage(messages.restrictions)}</WitLabel>
            <div>
              <label>
                <FormattedMessage {...messages.maxNights} />*
              </label>
              <InputGroup
                type="number"
                value={formData.maxNights}
                field={{ fieldName: "maxNights" }}
                onChange={data => handleChange(data, "maxNights")}
                disabled={false}
                readOnly={false}
              />
              <label>
                <FormattedMessage {...messages.maxNightsHelpText} />
              </label>
            </div>
            <div>
              <label>
                <FormattedMessage {...messages.maxAmount} />*
              </label>
              <InputGroup
                type="number"
                value={formData.maxAmount}
                field={{ fieldName: "maxAmount" }}
                onChange={data => handleChange(data, "maxAmount")}
                disabled={false}
                readOnly={false}
              />
              <label>
                <FormattedMessage {...messages.maxAmountHelpText} />
              </label>
            </div>
          </>
        )}
      </div>
    </>
  );
}
